/**
 * <cevo-signup-popup> – decides when the sign-up pop-up opens and remembers the visitor's answer.
 * Markup and settings: sections/cevo-signup-popup.liquid. The form inside is <cevo-optin-form>
 * (cevo-optin-form.js), which does the validation, the bot check and the sending.
 *
 * Opens after a delay or a scroll depth, whichever comes first, or on exit intent (desktop only).
 * Once seen it stays away for data-dismiss-days, and for a year after a sign-up here or through the
 * band above the footer. It never opens for
 * visitors arriving from an Omnisend email or already known to Omnisend (omnisendContactID
 * cookie). Add ?cevo_popup=show to any URL to open it regardless, for testing.
 */
(() => {
  if (customElements.get('cevo-signup-popup')) return;

  const STORAGE_KEY = 'cevo-signup-popup';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const SIGNED_UP_DAYS = 365;
  const FROM_EMAIL_DAYS = 90;
  const DEFAULT_DISMISS_DAYS = 7;
  const BUSY_RETRY_MS = 4000;
  const FORCE_PARAM = 'cevo_popup';
  const OMNISEND_COOKIE = 'omnisendContactID';
  const SUCCESS_EVENT = 'cevo-optin:success';
  const DESKTOP_POINTER_QUERY = '(hover: hover) and (pointer: fine)';
  // Dawn puts one of these classes on <body> while its cart drawer, menu or search modal is open.
  const DAWN_OVERLAY_CLASSES = ['overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet'];

  /** localStorage can be missing or throw (private mode, blocked storage); the pop-up then just shows. */
  const store = {
    read() {
      try {
        const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        return value && typeof value === 'object' ? value : {};
      } catch {
        return {};
      }
    },
    write(changes) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...store.read(), ...changes }));
      } catch {
        // Nothing to do: without storage the visitor may simply see the pop-up again.
      }
    },
  };

  const numberFrom = (value, fallback) => {
    const parsed = Number.parseInt(value ?? '', 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };

  /** Arrived from an Omnisend email (Omnisend adds these UTM tags), or Omnisend already knows them. */
  function isKnownToOmnisend(params) {
    const fromEmail = params.get('utm_source') === 'omnisend' || params.get('utm_medium') === 'email';
    if (fromEmail) store.write({ hiddenUntil: Date.now() + FROM_EMAIL_DAYS * DAY_MS });
    const hasContactCookie = document.cookie.split(';').some((part) => part.trim().startsWith(`${OMNISEND_COOKIE}=`));
    return fromEmail || hasContactCookie;
  }

  const isDawnOverlayOpen = () => DAWN_OVERLAY_CLASSES.some((name) => document.body.classList.contains(name));

  /** Custom events for GA4 (dataLayer) and Shopify customer events, where either is present. */
  function track(name, detail) {
    window.dataLayer?.push({ event: name, ...detail });
    window.Shopify?.analytics?.publish?.(name, detail);
  }

  class CevoSignupPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.signup = this.querySelector('cevo-optin-form');
      if (!this.dialog || !this.signup) return;

      this.isDesignMode = this.hasAttribute('data-design-mode');
      this.cleanups = [];
      this.triggerCleanups = [];
      this.variant = this.chooseVariant();
      this.listen(this.dialog, 'click', this.onDialogClick);
      // On the document, so a sign-up through the band above the footer also keeps the pop-up away.
      this.listen(document, SUCCESS_EVENT, this.onSignedUp);

      if (this.isDesignMode) {
        this.listenForThemeEditor();
        return;
      }

      const params = new URLSearchParams(window.location.search);
      if (params.get(FORCE_PARAM) === 'show') {
        this.open('forced');
        return;
      }
      if (isKnownToOmnisend(params) || (store.read().hiddenUntil ?? 0) > Date.now()) return;

      this.preloadImage();
      this.armTriggers();
    }

    disconnectedCallback() {
      this.disarmTriggers();
      this.cleanups?.forEach((cleanup) => cleanup());
      this.cleanups = [];
    }

    listen(target, type, handler, options, cleanups = this.cleanups) {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    /** A/B split only runs when a variant B heading is set. The choice sticks per visitor. */
    chooseVariant() {
      const variantHeading = this.dataset.variantBHeading;
      const source = this.dataset.source || 'popup';
      if (!variantHeading) return null;

      const saved = store.read().variant;
      const variant = saved === 'a' || saved === 'b' ? saved : Math.random() < 0.5 ? 'a' : 'b';
      store.write({ variant });
      this.signup.dataset.source = `${source}-${variant}`;

      if (variant === 'b' && !this.isDesignMode) {
        const heading = this.querySelector('[data-popup-heading]');
        const text = this.querySelector('[data-popup-text]');
        if (heading) heading.textContent = variantHeading;
        if (text && this.dataset.variantBText) text.textContent = this.dataset.variantBText;
      }
      return variant;
    }

    armTriggers() {
      const delaySeconds = numberFrom(this.dataset.delaySeconds, 0);
      const scrollPercent = numberFrom(this.dataset.scrollPercent, 0);

      if (delaySeconds > 0) this.after(delaySeconds * 1000, () => this.open('delay'));
      if (scrollPercent > 0) {
        const onScroll = () => this.onScroll(scrollPercent);
        this.listen(window, 'scroll', onScroll, { passive: true }, this.triggerCleanups);
      }
      if (this.dataset.exitIntent === 'true' && window.matchMedia(DESKTOP_POINTER_QUERY).matches) {
        this.listen(document, 'mouseout', this.onMouseOut, undefined, this.triggerCleanups);
      }
    }

    disarmTriggers() {
      this.triggerCleanups?.forEach((cleanup) => cleanup());
      this.triggerCleanups = [];
    }

    after(ms, callback) {
      const timer = window.setTimeout(callback, ms);
      this.triggerCleanups.push(() => window.clearTimeout(timer));
    }

    onScroll(scrollPercent) {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && (window.scrollY / scrollable) * 100 >= scrollPercent) this.open('scroll');
    }

    // Leaving through the top of the window: towards the tabs, address bar or close button.
    onMouseOut = (event) => {
      if (!event.relatedTarget && event.clientY <= 0) this.open('exit-intent');
    };

    /** Start the image download once the pop-up is likely to open, so it does not pop in. */
    preloadImage() {
      const image = this.querySelector('.cevo-popup__image');
      if (image) image.loading = 'eager';
    }

    open(trigger) {
      if (this.dialog.open) return;
      // Never on top of the cart drawer or menu: try again shortly instead.
      if (!this.isDesignMode && trigger !== 'forced' && isDawnOverlayOpen()) {
        this.after(BUSY_RETRY_MS, () => this.open(trigger));
        return;
      }
      if (!this.isDesignMode) {
        this.disarmTriggers();
        const dismissDays = numberFrom(this.dataset.dismissDays, DEFAULT_DISMISS_DAYS);
        store.write({ hiddenUntil: Date.now() + dismissDays * DAY_MS });
      }

      this.preloadImage();
      this.dialog.showModal();
      this.signup.prepareVerification?.();
      if (!this.isDesignMode) track('cevo_popup_view', { trigger, variant: this.variant ?? 'none' });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    // The backdrop counts as a click on the <dialog> itself; the panel fills the rest of it.
    onDialogClick = (event) => {
      if (event.target === this.dialog || event.target.closest('[data-popup-close]')) this.close();
    };

    // signedUpUntil also hides the band above the footer (sections/cevo-signup-band.liquid).
    onSignedUp = (event) => {
      if (this.isDesignMode) return;
      const until = Date.now() + SIGNED_UP_DAYS * DAY_MS;
      store.write({ hiddenUntil: until, signedUpUntil: until });
      this.disarmTriggers();
      if (this.contains(event.target)) track('cevo_popup_signup', { variant: this.variant ?? 'none' });
    };

    // In the theme editor the pop-up opens while its section is selected (and after each re-render
    // while editing it), so the copy can be edited in place.
    listenForThemeEditor() {
      const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
      const isThisSection = (event) => event.detail?.sectionId === sectionId;
      const openHere = (event) => isThisSection(event) && this.open('editor');
      this.listen(document, 'shopify:section:select', openHere);
      this.listen(document, 'shopify:section:load', openHere);
      this.listen(document, 'shopify:section:deselect', (event) => isThisSection(event) && this.close());
    }
  }

  customElements.define('cevo-signup-popup', CevoSignupPopup);
})();
